import { useMemo, useState, useEffect } from 'react';
import { getNodeWidth, getNodeHorizontalMargin } from '../utils';

interface IUserSliderPagerOption {
  containerNode?: React.RefObject<HTMLElement> | null;
  nodes?: React.RefObject<(HTMLElement | null)[]>;
  updateDeps?: any[];
}

export default function useSliderPager(option: IUserSliderPagerOption) {
  const { containerNode, nodes, updateDeps } = option ?? {};
  const [isShowPager, setIsShowPager] = useState(false);
  const [current, setCurrent] = useState(0);
  const [distances, setDistances] = useState<number[]>([]);

  const slideDistance = useMemo<number>(() => {
    if (!current) return 0;
    if (!distances?.length) return 0;
    return distances[current - 1];
  }, [current, distances]);

  const handleCurrentChange = (payload: number) => {
    setCurrent(payload);
  };

  const updateSlideInfo = () => {
    const containerNodeTmp = containerNode?.current;
    const nodesTmp = nodes?.current;
    if (!containerNodeTmp || !nodesTmp?.length) {
      setCurrent(0);
      setDistances([0]);
      return;
    }
    const containerWidth = containerNodeTmp.clientWidth;
    const distancesTmp: number[] = [0];
    let width = 0;
    for (const nodeTmp of nodesTmp) {
      const nodeWidth = getNodeWidth(nodeTmp);
      const nodeHorizontalMargin = getNodeHorizontalMargin(nodeTmp);
      const itemWidth = nodeWidth + nodeHorizontalMargin;
      if (itemWidth > containerWidth) {
        let prevDistance = distancesTmp[distancesTmp.length - 1];
        // 上一个元素的宽度，在拆分单个元素宽度前，需要加入分页中，作为一页，拆分的元素 从开始位置 计算分页
        if (width > 0) {
          prevDistance = prevDistance + width;
          distancesTmp.push(prevDistance);
        }
        // 单个元素宽度 可以拆分成 多少个完整的容器宽度（按照最大宽度即容器宽度 拆分 单个元素的宽度）
        const num = Math.floor(itemWidth / containerWidth);
        // 单个元素 按照容器宽度拆分后，剩余的宽度（剩余宽度不足一倍容器的宽度）
        const mod = itemWidth % containerWidth;
        const itemDistances = Array.from({ length: num }).map((item, idx) => prevDistance + containerWidth * (idx + 1));
        if (mod) {
          const prevItemDistance = itemDistances[itemDistances.length - 1] ?? prevDistance;
          itemDistances.push(prevItemDistance + mod);
        }
        Array.prototype.push.apply(distancesTmp, itemDistances);
        // 完整拆分后，设置下一轮 宽度累计的初始值为0
        width = 0;
      } else {
        width = width + itemWidth;
        if (width === containerWidth) {
          distancesTmp.push(containerWidth);
          width = 0;
        } else if (width > containerWidth) {
          const prevDistance = distancesTmp[distancesTmp.length - 1];
          const currentDistance = prevDistance + (width - itemWidth);
          distancesTmp.push(currentDistance);
          /**
           * 因为这里就是因为当前的宽度累计 加上来itemWidth 导致 超出了容器的宽度，而itemWidth所属的node，所以node应该要放入下一页去，所以itemWidth应该要作为下一轮宽度累计的初始值
           * 将itemWidth纳入下一轮的宽度累计，并作为初始值
           */
          width = itemWidth;
        }
      }
    }
    setDistances(distancesTmp);
  };

  /**
   * 更新分页相关数据（重置页码为第一页，每页需要滚动的距离）
   * 1、需要通过setTimeout方式，在下一帧渲染后访问dom的属性，否则获取到的containerWidth不是最新的
   * 2、useEffect的依赖项需要加入 distances.length
   *    这个是翻页的总页数（会影响分也器的宽度，间接影响containerWidth）
   *    所以需要监听distances.length，在updateSlideInfo方法内 更新 distances后,监听到distances.length变化，再重新调用updateSlideInfo方法 更新翻页相关的数据
   *    第一次调用updateSlideInfo方法 更新分页数据后，页面刷新（以后就可以拿到containerWidth的比较精确的值了）
   */
  useEffect(() => {
    const containerNodeTmp = containerNode?.current;
    if (!isShowPager || !containerNodeTmp) {
      setCurrent(0);
      setDistances([0]);
      return;
    }
    setTimeout(() => {
      if (isShowPager) {
        setCurrent(1);
        updateSlideInfo();
      } else {
        setCurrent(0);
        setDistances([0]);
      }
    }, 100);
  }, [isShowPager, distances?.length]);

  // 更新是否需要展示翻页器
  useEffect(() => {
    const containerNodeTmp = containerNode?.current;
    if (!containerNodeTmp) {
      setIsShowPager(false);
      return;
    }
    const containerClientWidth = containerNodeTmp.clientWidth;
    const containerScrollWidth = containerNodeTmp.scrollWidth;
    setIsShowPager(containerScrollWidth > containerClientWidth);
  }, [updateDeps]);

  return {
    slideDistance,
    isShowPager,
    current,
    total: distances?.length,
    onCurrentChange: handleCurrentChange,
  };
}
